import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { SketchPicker } from "@hello-pangea/color-picker";

import { GET, PUT, httpOk } from 'helpers/api'

import {
	DndContext,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	useSortable,
	rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatMoney } from 'helpers/helpers';

function Index() {
	const { t } = useTranslation()

	const sensors = useSensors(
		useSensor(PointerSensor)
	);

	const colorRef = useRef(null);
	const bgColorRef = useRef(null);

	const [menu, setMenu] = useState([])
	const [menuDuplicate, setMenuDuplicate] = useState([]);
	const [menuParent, setMenuParent] = useState({ 'parent_id': null });
	const [products, setProducts] = useState([])
	const [selectedItem, setSelectedItem] = useState({
		color: '',
		bg_color: '',
		order_number: '',
	})

	async function update() {
		var menuCopy = [...menu]

		if (selectedItem.type === "menu") {
			await PUT('/menu-order-color', {
				'menu_id': selectedItem.id,
				'color': selectedItem.color ?? null,
				'bg_color': selectedItem.bg_color ?? null,
				'all_menus': selectedItem.all_menus ?? false,
			}, { loader: true })

			menuCopy[selectedItem.index] = selectedItem

			if (selectedItem.all_menus) {
				for (let i = 0; i < menuCopy.length; i++) {
					menuCopy[i]['color'] = selectedItem.color ?? null
					menuCopy[i]['bg_color'] = selectedItem.bg_color ?? null
				}
			}

			var sortedMenu = menuCopy.sort(compareAge)

			setMenu(sortedMenu)
		}

		if (selectedItem.type === "product") {
			var productsCopy = [...products]

			await PUT('/products-order-color', {
				'menu_id': selectedItem.menu_id,
				'product_id': selectedItem.product_id,
				'color': selectedItem.color ?? null,
				'bg_color': selectedItem.bg_color ?? null,
				'all_products': selectedItem.all_products ?? false,
			}, { loader: true })

			productsCopy[selectedItem.index] = selectedItem

			if (selectedItem.all_products) {
				for (let i = 0; i < productsCopy.length; i++) {
					productsCopy[i]['color'] = selectedItem.color ?? null
					productsCopy[i]['bg_color'] = selectedItem.bg_color ?? null
				}
			}
			var sortedProducts = productsCopy.sort(compareAge)

			setProducts(sortedProducts)

			for (let i = 0; i < menuCopy.length; i++) {
				for (let j = 0; j < menuCopy[i]['products'].length; j++) {
					if (menuCopy[i]['products'][j]['product_id'] === selectedItem.product_id) {
						menuCopy[i]['products'][j] = selectedItem
					}
				}
			}

			setMenu(menuCopy)
		}
	}

	function compareAge(a, b) {
		return a.order_number - b.order_number;
	}

	function selectMenu(item, index) {
		var menuCopy = [...menu]
		for (let i = 0; i < menuCopy.length; i++) {
			if (i === index) {
				menuCopy[i]['active'] = true
				setProducts(menuCopy[i]['products'])
			} else {
				menuCopy[i]['active'] = false
			}
		}

		menuCopy[index]['products'] = menuCopy[index]['products'].sort(compareAge)

		if (menuCopy[index]?.children.length === 0) setMenu(menuCopy)

		if (menuCopy[index]?.children.length !== 0) {
			setMenu(menuCopy[index]['children'])
			setMenuParent({
				'parent_id': menuCopy[index]['id'],
				'color': menuCopy[index]['color'] ?? null,
				'bg_color': menuCopy[index]['bg_color'] ?? null,
			})
		}

		var itemCopy = { ...item }
		itemCopy.color_bool = false
		itemCopy.bg_color_bool = false
		itemCopy.color = itemCopy.color ? itemCopy.color : ''
		itemCopy.bg_color = itemCopy.bg_color ? itemCopy.bg_color : ''
		itemCopy.order_number = itemCopy.order_number ? itemCopy.order_number : ''
		itemCopy.index = index
		itemCopy.type = 'menu'
		setSelectedItem(itemCopy)
	}

	function handleBackClick() {
		// Мы уже на самом верхнем уровне (Главное меню), нет смысла идти назад
		if (menuParent.parent_id === null) return;

		// Находим родительский узел текущего уровня
		const parentNode = findNodeById(menuDuplicate, menuParent.parent_id);

		if (parentNode && parentNode.parent_id !== undefined) { // Проверяем, что родительский узел найден и у него есть parent_id (то есть это не корень)
			const grandparentNode = findNodeById(menuDuplicate, parentNode.parent_id);

			if (grandparentNode) {
				setMenu(grandparentNode.children); // Показываем дочерние элементы дедушки
				setMenuParent({
					'parent_id': grandparentNode.id,
					'color': grandparentNode.color ?? null,
					'bg_color': grandparentNode.bg_color ?? null,
				}); // Дедушка становится новым родителем
			} else {
				// Если дедушка не найден (значит, текущий родитель - это корневой элемент, например "Main")
				setMenu(menuDuplicate.filter(node => node.parent_id === null)); // Показываем все корневые элементы
				setMenuParent({}); // Переходим на уровень без родителя
			}
		}
	}

	function selectProduct(item, index) {
		var itemCopy = { ...item }

		itemCopy.color_bool = false
		itemCopy.bg_color_bool = false
		itemCopy.color = itemCopy.color ? itemCopy.color : ''
		itemCopy.bg_color = itemCopy.bg_color ? itemCopy.bg_color : ''
		itemCopy.order_number = itemCopy.order_number ? itemCopy.order_number : ''
		itemCopy.index = index
		itemCopy.type = 'product'

		setSelectedItem(itemCopy)
	}

	function findNodeById(nodes, id) {
		for (const node of nodes) {
			if (node.id === id) {
				return node;
			}

			if (node.children && node.children.length > 0) {
				const found = findNodeById(node.children, id);
				if (found) {
					return found;
				}
			}
		}

		return null;
	}

	async function getMenu() {
		const response = await GET('/delivery/menu', {}, { loader: true })
		if (httpOk(response)) {
			setMenu(response.data.menu)
			setMenuDuplicate(response.data.menu)
		}
	}

	function handleInputChange(key, value) {
		var selectedItemCopy = { ...selectedItem }
		selectedItemCopy[key] = value
		setSelectedItem(selectedItemCopy)
	}

	function handleChangeBgColor(color) {
		handleInputChange('bg_color', color.hex)
	}

	function handleChangeColor(color) {
		handleInputChange('color', color.hex)
	}

	async function updateMenuOrderNumber(item, newIndex) {
		await PUT('/menu-order-number', {
			'id': item.id,
			'order_number': newIndex,
		}, { loader: true })
	}

	async function updateProductOrderNumber(item, newIndex) {
		await PUT('/products-order-number', {
			'product_id': item.product_id,
			'menu_id': item.menu_id,
			'order_number': newIndex,
		}, { loader: true })
	}

	async function handleMenuDragEnd(event) {
		const { active, over } = event;

		// 1. Если бросили вне списка — просто выходим
		if (!over) return;

		// 2. Если бросили на себя — просто select
		if (active.id === over.id) {
			const index = menu.findIndex(item => item.id === active.id);
			selectMenu(menu[index], index);
			return;
		}

		// 3. Реальный reorder
		setMenu((items) => {
			const oldIndex = items.findIndex(item => item.id === active.id);
			const newIndex = items.findIndex(item => item.id === over.id);

			if (oldIndex === -1 || newIndex === -1) return items;

			const movedItem = { ...items[oldIndex] }; // ОБЯЗАТЕЛЬНО КЛОН
			movedItem.order_number = newIndex;

			updateMenuOrderNumber(movedItem, newIndex);

			return arrayMove(items, oldIndex, newIndex);
		});
	}

	const handleProductDragEnd = (event) => {
		const { active, over } = event;

		if (active && over && active.id !== over.id) {
			setProducts((items) => {
				// Ищем индексы по product_id
				const oldIndex = items.findIndex((item) => item.product_id === active.id);
				const newIndex = items.findIndex((item) => item.product_id === over.id);

				const movedItem = items[oldIndex];

				// Обновляем локально и отправляем на сервер
				const updatedItem = { ...movedItem, order_number: newIndex };
				updateProductOrderNumber(updatedItem, newIndex);

				return arrayMove(items, oldIndex, newIndex);
			});
		} else if (active) {
			// Логика обычного клика (выбора)
			const index = products.findIndex((item) => item.product_id === active.id);
			selectProduct(products[index], index);
		}
	};

	const SortableItem = ({ item, id }) => {
		const {
			attributes,
			listeners,
			setNodeRef,
			transform,
			transition,
		} = useSortable({ id });

		return (
			<div
				ref={setNodeRef}
				className="menu-box"
				style={{
					transform: CSS.Transform.toString(transform),
					transition,
					'position': 'relative',
					'color': (item.color ? item.color : 'var(--dark-text-strong, #1e1d1d)'),
					'backgroundColor': (item.bg_color ? item.bg_color : 'var(--dark-surface-3, #eee)'),
				}}
				{...attributes}
				{...listeners}>
				{item.name}
				{item.sale_price &&
					<div className="order-saleprice-info">
						{formatMoney(item.sale_price)}
					</div>
				}
			</div>
		);
	}

	const handleClose = () => {
		setSelectedItem({ ...selectedItem, 'color_bool': false, 'bg_color_bool': false })
	};

	useEffect(() => {
		getMenu()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		function handleClickOutside(event) {
			if (colorRef.current && !colorRef.current.contains(event.target)) {
				handleClose()
			}
			if (bgColorRef.current && !bgColorRef.current.contains(event.target)) {
				handleClose()
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [selectedItem, colorRef, bgColorRef]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="card" style={{ 'minHeight': '500px' }}>
				<div className="card-body">
					{true &&
						<>
							<div className="d-flex">
								<div className="menu-box"
									style={{
										'backgroundColor': (selectedItem.bg_color ? selectedItem.bg_color : 'var(--dark-surface-3, #eee)'),
										'color': (selectedItem.color ? selectedItem.color : 'var(--dark-text-strong, #1e1d1d)')
									}}>
									{selectedItem.name}
								</div>
								<div>
									<div className="d-flex gap-2">
										<div className="form-group">
											<div className="position-relative">
												<input type="text" className="form-control"
													placeholder={t('color')}
													value={selectedItem.color}
													onChange={(e) => handleInputChange('color', e.target.value)}
												/>
												<div className="color-picker-icon" style={{ backgroundColor: selectedItem.color }}
													onClick={() => setSelectedItem({ ...selectedItem, 'color_bool': !selectedItem.color_bool })}
												/>
											</div>
											{selectedItem.color_bool &&
												<div ref={colorRef}>
													<SketchPicker
														className="color-picker-body"
														onChangeComplete={handleChangeColor}
														color={selectedItem.color}
													/>
												</div>
											}
										</div>
										<div className="form-group">
											<div className="position-relative">
												<input type="text" className="form-control"
													placeholder={t('bg_color')}
													value={selectedItem.bg_color} onChange={(e) => handleInputChange('bg_color', e.target.value)} />
												<div className="color-picker-icon" style={{ backgroundColor: selectedItem.bg_color }}
													onClick={() => setSelectedItem({ ...selectedItem, 'bg_color_bool': !selectedItem.bg_color_bool })}
												/>
											</div>
											{selectedItem.bg_color_bool &&
												<div ref={bgColorRef}>
													<SketchPicker
														className="color-picker-body"
														onChangeComplete={handleChangeBgColor}
														color={selectedItem.bg_color}
													/>
												</div>
											}
										</div>
									</div>
									<div className="d-flex justify-content-between">
										<div className="d-flex flex-column">
											<div className="d-flex justify-content-between gap-2 w-100">
												<h6>{t('change_all_menu')}</h6>
												<div className="form-check form-switch form-switch-sm">
													<input type="checkbox" className="form-check-input" name="all_menus"
														checked={selectedItem.all_menus}
														onChange={(e) => setSelectedItem({ ...selectedItem, 'all_menus': !selectedItem.all_menus })}
													/>
												</div>
											</div>
											<div className="d-flex justify-content-between gap-2 w-100">
												<h6>{t('change_all_products')}</h6>
												<div className="form-check form-switch form-switch-sm">
													<input type="checkbox" className="form-check-input" name="all_products"
														checked={selectedItem.all_products}
														onChange={(e) => setSelectedItem({ ...selectedItem, 'all_products': !selectedItem.all_products })}
													/>
												</div>
											</div>
										</div>
										<div className="form-group">
											<button className="btn btn-success"
												onClick={() => update()}>
												{t('save')}
											</button>
										</div>
									</div>

								</div>
							</div>
							<hr />
						</>
					}

					<div className="d-flex">
						<div className="menu-wrapper">
							{menuParent.parent_id && (
								<div className="order-item-box-category me-2"
									style={{
										'width': '100%',
										'height': '50px',
										'marginBottom': '5px',
										'color': 'var(--dark-text-strong, #1e1d1d)',
										'backgroundColor': 'var(--dark-surface-3, #eee)',
									}}
									onClick={() => handleBackClick()}>
									<div className="d-flex cursor">
										<i className="uil uil-angle-left fz30"></i>
										<div className="vertical-center">{t('back')}</div>
									</div>
								</div>
							)}
							<DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								onDragEnd={handleMenuDragEnd}>
								<SortableContext
									items={menu}
									strategy={rectSortingStrategy}>
									{menu.map((item) => (
										<SortableItem
											key={item.id}
											id={item.id}
											item={item}
										/>
									))}
								</SortableContext>
							</DndContext>
						</div>

						<div className="menu-wrapper">
							{products?.length > 0 &&
								<DndContext
									sensors={sensors}
									collisionDetection={closestCenter}
									onDragEnd={handleProductDragEnd}>
									<SortableContext
										items={products.map(p => p.product_id)}
										strategy={rectSortingStrategy}>
										{products?.map((item) => (
											<SortableItem
												key={item.product_id}
												id={item.product_id}
												item={item}
											/>
										))}
									</SortableContext>
								</DndContext>
							}
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export default Index